import helpTopics from "@volca/help/topics.json";
import type { BusinessProduct } from "@/business/business";
import {
  isProductEnabled,
  isProductFeatureEnabled,
} from "@/business/product-access";

export interface HelpTopic {
  id: string;
  title: string;
  product?: BusinessProduct;
  featurePath?: string[];
  isSection?: boolean;
  children?: HelpTopic[];
}

export const HELP_TOPICS = helpTopics as HelpTopic[];

const flattenGuides = (topics: HelpTopic[]): HelpTopic[] =>
  topics.flatMap((topic) => [
    ...(topic.isSection ? [] : [topic]),
    ...flattenGuides(topic.children ?? []),
  ]);

export const HELP_GUIDE_IDS = flattenGuides(HELP_TOPICS).map(
  (topic) => topic.id,
);

export const getAvailableHelpTopics = (settings: unknown): HelpTopic[] => {
  const filterTopics = (
    topics: HelpTopic[],
    product?: BusinessProduct,
  ): HelpTopic[] =>
    topics.flatMap((topic) => {
      const effectiveProduct = topic.product ?? product;
      if (effectiveProduct && !isProductEnabled(settings, effectiveProduct))
        return [];
      if (
        topic.featurePath &&
        (!effectiveProduct ||
          !isProductFeatureEnabled(
            settings,
            effectiveProduct,
            ...topic.featurePath,
          ))
      )
        return [];
      return [
        {
          ...topic,
          children: topic.children
            ? filterTopics(topic.children, effectiveProduct)
            : undefined,
        },
      ];
    });
  return filterTopics(HELP_TOPICS);
};

export const getHelpTopicTrail = (
  topics: HelpTopic[],
  id: string,
): HelpTopic[] => {
  for (const topic of topics) {
    if (topic.id === id) return [topic];
    const trail = getHelpTopicTrail(topic.children ?? [], id);
    if (trail.length) return [topic, ...trail];
  }
  return [];
};

export const getFirstHelpGuide = (
  topics: HelpTopic[],
): HelpTopic | undefined => {
  for (const topic of topics) {
    if (!topic.isSection) return topic;
    const guide = getFirstHelpGuide(topic.children ?? []);
    if (guide) return guide;
  }
  return undefined;
};

export const getHelpGuideNeighbors = (
  topics: HelpTopic[],
  currentId: string,
): { previous?: HelpTopic; next?: HelpTopic } => {
  const product = getHelpTopicTrail(topics, currentId)[0];
  if (!product) return {};
  const guides = flattenGuides([product]);
  const index = guides.findIndex((guide) => guide.id === currentId);
  if (index < 0) return {};
  return { previous: guides[index - 1], next: guides[index + 1] };
};
